import { Component, TemplateFunction } from '@ribajs/core'
import { EventDispatcher } from '@ribajs/events'
import { getUID } from '@ribajs/utils'

import type { ShopifyImage, SidebarState } from '../types/index.js'

/**
 * The types for the scope
 */
interface Scope {
  // METHODS

  show: SbPopupComponent['show']
  hide: SbPopupComponent['hide']
  toggle: SbPopupComponent['toggle']
  onImageLoaded: SbPopupComponent['onImageLoaded']

  // OPTIONS

  /** if true, then the popup is displayed */
  visible: boolean
  /** if true, the popup will always be displayed (for testing / debugging) */
  forceShow: boolean
  /** If `true` is only displayed on the first visit to the page */
  showOnlyOnInitialVisit: boolean
  /** Popup title id, used for the `aria-labelledby` attribute */
  titleId: string
  /** Delay until the popup is shown (in milliseconds), can be used in combination with `showOnlyOnInitialVisit` */
  delay: number
  /** counts how many times the popup was shown */
  showCount: number
  /** Optional additional data from the sb-popup-button */
  additionalData?: any
  /** The variant of the popup */
  variant: string

  // TEMPLATES

  /** The body html, you can also use the Riba template engine here */
  bodyTemplate?: string
  /** The title html or string */
  title: string
  /** The aria-label for the popup, used for accessibility */
  ariaLabel?: string
}

/**
 * A simple popup component
 */
export class SbPopupComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-popup>...</sb-popup>`
   */
  public static tagName = 'sb-popup'
  private trapFocusHandler?: (event: KeyboardEvent) => void

  events = EventDispatcher.getInstance()

  /**
   * Everything that is assigned to the scope can be used within the template with the Riba template engine.
   * Examples:
   * - Methods can be called with `<div rv-on-click="show"></div>`, this will call the show method defined in the scope
   * - Properties can be used with `<div rv-click-active="visible"></div>`, this will add the class `active` if visible is `true` and remove the class `active` if visible is `false`,
   * - Templates can be used with `<div rv-template="body"></div>`, `<div rv-html="body"></div>` or `<div rv-text="body"></div>`, this will be add the template, html or string to the innerHTML of the element. But you can only use the Riba Template Engine with `rv-template`
   */
  public scope: Scope = {
    // METHODS

    onImageLoaded: this.onImageLoaded,
    show: this.show,
    hide: this.hide,
    toggle: this.toggle,

    // OPTIONS

    visible: false,
    forceShow: false,
    showOnlyOnInitialVisit: false,
    titleId: '',
    delay: 0,
    showCount: 0,
    additionalData: {},
    variant: 'white',
    // TEMPLATES

    bodyTemplate: undefined,
    title: '',
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return [
      'visible',
      'force-show',
      'body-template',
      'title',
      'aria-label',
      'show-only-on-initial-visit',
      'delay',
      'variant',
    ]
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return []
  }

  public onImageLoaded(
    image: (ShopifyImage & { loaded?: boolean }) | undefined,
    eventName: 'changed' | 'loaded',
  ) {
    if (!image) return
    image.loaded = eventName === 'loaded'

    // WORKAROUND: Loading timeout
    setTimeout(() => {
      image.loaded = true
    }, 3000)
  }

  /**
   * Open / show this popup
   * @param event
   * @param data Additional data to be included in the scope property `additionalData`
   * @param force If true, the popup will be shown without delay
   */
  public show(event?: Event, data?: any, force = false) {
    if (data) {
      this.scope.additionalData = data
    }
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '-1')
    }
    const delay = force ? 0 : this.scope.delay || 0
    setTimeout(() => {
      event?.preventDefault()
      this.scope.visible = true
      this.onStateChange()
      this.focus({ preventScroll: true })
      this.trapFocus(this)
      this.scope.showCount++
    }, delay)
  }

  /**
   * Close / hide this popup
   * @param event
   * @param data Additional data to be included in the scope property `additionalData`
   */
  public hide(event?: Event, data?: any) {
    if (data) {
      this.scope.additionalData = data
    }
    event?.preventDefault()
    this.scope.visible = false
    this.onStateChange()
    if (this.trapFocusHandler) {
      this.removeEventListener('keydown', this.trapFocusHandler)
      this.trapFocusHandler = undefined
    }
  }

  /**
   * Toggle the visibility of the popup
   * @param event
   * @param data Additional data to be included in the scope property `additionalData`
   * @param force If true, the popup will be shown without delay
   */
  public toggle(event?: Event, data?: any, force = false) {
    if (this.scope.visible) {
      this.hide(event, data)
    } else {
      this.show(event, data, force)
    }
  }

  public getState() {
    return {
      visible: this.scope.visible,
    }
  }

  constructor() {
    super()
    this.onKeyup = this.onKeyup.bind(this)
  }

  /**
   * Called as soon as this custom element is detected in the DOM
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbPopupComponent.observedAttributes)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected onSidebarStateChanged(
    state: SidebarState,
    //sidebar: SbSidebarComponent,
  ) {
    if (state !== 'hidden') {
      this.hide()
    }
  }

  protected trapFocus(element: HTMLElement) {
    // Remove any existing trap focus handler
    if (this.trapFocusHandler) {
      element.removeEventListener('keydown', this.trapFocusHandler)
    }

    const getFocusableElements = () => {
      const elements = Array.from(
        element.querySelectorAll(
          'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(el => {
        const htmlEl = el as HTMLElement
        const style = window.getComputedStyle(htmlEl)

        const isVisible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          htmlEl.offsetWidth > 0 &&
          htmlEl.offsetHeight > 0

        const isNotDisabled =
          !htmlEl.hasAttribute('disabled') &&
          !(htmlEl as any).disabled &&
          !htmlEl.hasAttribute('aria-disabled')

        return isVisible && isNotDisabled
      }) as HTMLElement[]

      // For radio buttons, only include the checked one or the first one in each group
      const radioGroups = new Map<string, HTMLInputElement>()
      const filteredElements: HTMLElement[] = []

      elements.forEach(el => {
        if (
          el.tagName === 'INPUT' &&
          (el as HTMLInputElement).type === 'radio'
        ) {
          const radio = el as HTMLInputElement
          const groupName = radio.name

          if (!radioGroups.has(groupName)) {
            // Add the first radio of the group, or the checked one if it exists
            const checkedRadio = elements.find(
              r =>
                r.tagName === 'INPUT' &&
                (r as HTMLInputElement).type === 'radio' &&
                (r as HTMLInputElement).name === groupName &&
                (r as HTMLInputElement).checked,
            ) as HTMLInputElement

            radioGroups.set(groupName, checkedRadio || radio)
          }
        } else {
          filteredElements.push(el)
        }
      })

      // Add the representative radio buttons
      radioGroups.forEach(radio => filteredElements.push(radio))

      return filteredElements
    }

    this.trapFocusHandler = (event: KeyboardEvent) => {
      // Let radio group navigation handle its own keys
      if (
        event.target &&
        (event.target as HTMLElement).tagName === 'INPUT' &&
        (event.target as HTMLInputElement).type === 'radio' &&
        ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)
      ) {
        return
      }

      if (event.key !== 'Tab') return

      const focusableElements = getFocusableElements()

      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey) {
        if (
          document.activeElement === firstElement ||
          !element.contains(document.activeElement)
        ) {
          lastElement.focus()
          event.preventDefault()
        }
      } else {
        if (
          document.activeElement === lastElement ||
          !element.contains(document.activeElement)
        ) {
          firstElement.focus()
          event.preventDefault()
        }
      }
    }

    element.addEventListener('keydown', this.trapFocusHandler)
  }

  protected addEventListeners() {
    window.addEventListener('keydown', this.onKeyup, { passive: true })
    this.events.on('sidebar-state-changed', this.onSidebarStateChanged, this)
  }

  protected removeEventListeners() {
    window.removeEventListener('keydown', this.onKeyup)
    this.events.off('sidebar-state-changed', this.onSidebarStateChanged, this)
  }

  /**
   * Called before this custom element is bound and ready
   */
  async beforeBind() {
    this.initPopup()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.showOnInitialVisit()
    if (this.scope.forceShow) {
      this.show(undefined, undefined, true)
    }
    this.addEventListeners()
    this.showMaskIfPopupAlreadyVisible()
  }

  /**
   * Called after all current components are bound and ready
   */
  async afterAllBind() {
    this.showMaskIfPopupAlreadyVisible()
  }

  /**
   * WORKAROUND: Show the mask if the popup is initially visible
   */
  showMaskIfPopupAlreadyVisible() {
    setTimeout(() => {
      this.onStateChange()
      this.classList.add('opacity-100', `${SbPopupComponent.tagName}-ready`)
      this.classList.remove('opacity-0')
    }, 10)
  }

  protected onKeyup(event: KeyboardEvent) {
    switch (event.code) {
      case 'Escape':
        this.hide()
        break
    }
  }

  protected initPopup() {
    if (!this.id) {
      this.id = getUID(`sb-popup-`)
    }
    this.scope.titleId = `${this.id}-title`
    this.onStateChange()
  }

  protected showOnInitialVisit() {
    if (this.scope.showOnlyOnInitialVisit) {
      const key = `sb-popup-${this.id}`
      const doNotShowAgain = localStorage.getItem(key) === 'no-not-show-again'
      if (!doNotShowAgain) {
        this.show()
      }
    }
  }

  /**
   * Triggers a custom event `state-changed`
   */
  protected triggerState() {
    this.dispatchEvent(
      new CustomEvent('state-changed', {
        detail: this.getState(),
      }),
    )
  }

  /**
   * Called if state changes, e.g. by the `show` or `hide` methods.
   */
  protected onStateChange() {
    this.triggerState()

    // Do not show this popup again
    if (
      this.scope.showOnlyOnInitialVisit &&
      this.scope.showCount > 0 &&
      !this.getState().visible
    ) {
      const key = `sb-popup-${this.id}`
      localStorage.setItem(key, 'no-not-show-again')
    }
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
